import React, { useState } from "react";
import useHeatmap from "../../../../hooks/useHeatmap";
import { Skeleton } from "../../../ui/skeleton";

export default function HeatmapChart({ heatmapData, isLoading = false, className = "" }) {
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: "" });

  const { matrix, metadata, days, hours, getCellColor } = useHeatmap(heatmapData);

  const handleMouseEnter = (event, day, hour, value) => {
    const rect = event.target.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    setTooltip({
      show: true,
      x: rect.left + scrollLeft + rect.width / 2,
      y: rect.top + scrollTop - 10,
      content: {
        day,
        hour,
        value,
        time: `${hour}:00`,
      },
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, content: "" });
  };

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-4 sm:p-6 ${className}`}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Total call activity by day
          </h3>
        </div>
        <div className="w-full overflow-x-auto">
          <div className="min-w-max">
            <div className="flex mb-2">
              <div className="w-12 sm:w-16 flex-shrink-0"></div>
              <div
                className="grid gap-0.5 w-full"
                style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
              >
                {Array.from({ length: 24 }).map((_, index) => (
                  <div key={index} className="text-center">
                    <Skeleton className="h-3 w-6 mx-auto" />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              {days.map((day, dayIndex) => (
                <div key={day} className="flex items-center">
                  <div className="w-12 sm:w-16 flex-shrink-0 text-[10px] sm:text-xs text-gray-600 font-medium pr-2 text-right">
                    {day}
                  </div>
                  <div
                    className="grid gap-0.5 w-full"
                    style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
                  >
                    {Array.from({ length: 24 }).map((_, hourIndex) => (
                      <Skeleton key={hourIndex} className="aspect-square rounded-sm" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-[#0C0E12] border border-gray-200 dark:border-[#22262F] rounded-lg p-4 sm:p-6 ${className}`}
    >
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Total call activity by day
        </h3>
      </div>

      <div className="w-full overflow-x-auto">
        <div className="min-w-max">
          {/* Hour labels */}
          <div className="flex mb-2">
            <div className="w-12 sm:w-16 flex-shrink-0"></div>
            <div
              className="grid gap-0.5 text-xs text-gray-500 w-full"
              style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
            >
              {hours.map((hour, index) => (
                <div key={index} className="text-center">
                  <span className="text-[8px] sm:text-[10px] md:text-xs">{hour}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap grid */}
          <div className="space-y-1">
            {days.map((day, dayIndex) => (
              <div key={day} className="flex items-center">
                <div className="w-12 sm:w-16 flex-shrink-0 text-[10px] sm:text-xs text-gray-600 font-medium pr-2 text-right">
                  {day}
                </div>
                <div
                  className="grid gap-1 w-full"
                  style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
                >
                  {matrix[dayIndex].map((value, hourIndex) => (
                    <div
                      key={hourIndex}
                      className={`aspect-square rounded-sm ${getCellColor(value)} hover:ring-1 hover:ring-indigo-300 cursor-pointer transition-all`}
                      onMouseEnter={(e) => handleMouseEnter(e, day, hours[hourIndex], value)}
                      onMouseLeave={handleMouseLeave}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom Tooltip */}
      {tooltip.show && (
        <div
          className="fixed z-50 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div className="font-semibold">{tooltip.content.day}</div>
          <div className="text-gray-300">{tooltip.content.time}</div>
          <div className="font-medium">
            {tooltip.content.value} {tooltip.content.value === 1 ? "call" : "calls"}
          </div>

          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
}
